import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/enrichedSchema';
import { useHistory } from 'react-router-dom';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { activateOrDeactivateEnrichedSchema } from 'store/enrichedSchema/actions';
import { enrichedSchemaTableTitles } from 'constants.js';
import filterHelpers from 'store/helpers/filterHelpers';
import { TableComponentV2, ToolbarV2 } from 'components';
import { NearMeOutlined } from '@material-ui/icons';

const {
  getPaginatedEnrichedSchemas,
  unsetCurrentEnrichedSchema,
  setCurrentEnrichedSchema,
  unsetCurrentEnrichedSchemaDetails,
  setCurrentEnrichedSchemaDetails,
  updateEnrichedSchemaFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const EnrichedSchema = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    onClickToggleArchive
  } = filterHelpers();
  const action = getPaginatedEnrichedSchemas({ force: true });
  const schemas = useSelector(store => store.enrichedSchema.paginatedList);
  const isLoading = useSelector(store => store.enrichedSchema.gettingPaginated);
  const activateOrDeactivateSucceeded = useSelector(
    store => store.enrichedSchema.activateOrDeactivateSucceeded
  );
  const activateOrDeactivateFailed = useSelector(
    store => store.enrichedSchema.activateOrDeactivateFailed
  );
  const activateOrDeactivateFailedMessage = useSelector(
    store => store.enrichedSchema.activateOrDeactivateFailedMessage
  );
  const filters = useSelector(store => store.enrichedSchema.filters);

  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  useEffect(() => {
    dispatch(getPaginatedEnrichedSchemas({ force: true }));
  }, []);

  useEffect(() => {
    if (activateOrDeactivateSucceeded) {
      dispatch(
        getPaginatedEnrichedSchemas({ force: true })
      );
    }
  }, [activateOrDeactivateSucceeded]);

  useEffect(() => {
    setOpenSnackbar(activateOrDeactivateFailed);
  }, [activateOrDeactivateFailed]);

  const onAddSchema = () => {
    dispatch(unsetCurrentEnrichedSchema());
    dispatch(unsetCurrentEnrichedSchemaDetails());
  };

  const onEditSchema = schema => {
    dispatch(unsetCurrentEnrichedSchema());
    dispatch(setCurrentEnrichedSchema(schema));
  };

  const onCopySchema = uuid => {
    let copiedSchema = schemas.filter(schema => schema.uuid === uuid)[0];
    dispatch(
      setCurrentEnrichedSchemaDetails({
        name: copiedSchema.schemaName + '-copy',
        option: copiedSchema.enrichmentProduct,
        hasMovedToNextStep: true
      })
    );
    dispatch(
      setCurrentEnrichedSchema({
        ...copiedSchema,
        schemaName: copiedSchema.schemaName + '-copy',
        uuid: ''
      })
    );
    history.push('/enriched-schema/details');
  };

  const onArchiveOrActivateSchema = uuid => {
    let action = activateOrDeactivateEnrichedSchema({
      schemaId: uuid,
      activeStatus: filters?.displayActive
    });
    dispatch(action);
  };
  
  const onEdit = schema => {
    onEditSchema(schema);
    history.push(`/enriched-schema/design/${schema.uuid}`);
  };
  const onCopy = uuid => {
    onCopySchema(uuid);
  };
  const onClickArchive = id => {
    onArchiveOrActivateSchema(id);
  };
  const onClickAddSchema = () => {
    onAddSchema();
    history.push(`/enriched-schema/details`);
  };
  
  const getButtonsGroup = () => [
    {
      name: `Schema Configuration`,
      action: () => history.push('/schema'),
      startIcon: <NearMeOutlined fontSize="default" />
    },
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Enriched Schemas`,
      action: () => onClickToggleArchive(action, updateEnrichedSchemaFilters, { displayActive: !filters?.displayActive, pageNo: 0 }),
    },
    {
      name: 'Add Enriched Schema',
      action: () => onClickAddSchema()
    }
  ];

  const getActionButtonOptions = schema => [
    {
      name: 'Edit Enriched Schema',
      action: ['ACTION', () => onEdit(schema)]
    },
    {
      name: 'Copy Enriched Schema',
      action: ['ACTION', () => onCopy(schema.uuid)]
    },
    {
      name: filters?.displayActive ? 'Archive Enriched Schema' : 'Activate Enriched Schema',
      action: ['ACTION', () => onClickArchive(schema.uuid)]
    }
  ];

  const searchOptions = {
    searchPlaceholder: 'Search Enriched Schemas',
    searchAction: (payload) => dispatch(updateEnrichedSchemaFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedEnrichedSchemas({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={enrichedSchemaTableTitles}
          onDoubleClickRow={row => handleOnEditClick(row)}
          actionButtonOptions={row => getActionButtonOptions(row)}
          filteredRows={schemas}
          action={getPaginatedEnrichedSchemas({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateEnrichedSchemaFilters(payload))}
        />
      </div>
      <UserMessageSnackbar
        openSnackbar={openSnackbar && activateOrDeactivateFailedMessage !== ''}
        message={activateOrDeactivateFailedMessage}
        severity="error"></UserMessageSnackbar>
    </div>
  );
};

export default EnrichedSchema;
